import { AutoCompleteTextField } from '@modules/common/components/AutoCompleteTextField';
import { MultiColumnAutoComplete } from '@modules/common/components/MultiColumnAutoComplete';
import { TaxonomyNodeSuggestion } from '@modules/common/components/TaxonomyNodesAutoComplete/TaxonomyNodeSuggestion';
import { TaxonomyNodeSuggestionOption } from '@modules/common/components/TaxonomyNodesAutoComplete/types';
import { useFetchTaxonomySearchGroups } from '@modules/common/hooks/useFetchTaxonomySearchGroups';
import {
  AutocompleteInputChangeReason,
  AutocompleteProps,
  BoxProps,
  TextFieldProps,
  useControlled,
} from '@mui/material';
import { debounce } from 'lodash';
import pluralize from 'pluralize';
import { ReactNode, SyntheticEvent, useEffect, useMemo } from 'react';

interface TaxonomyNodesGroupsAutoCompleteProps
  extends Omit<
    AutocompleteProps<TaxonomyNodeSuggestionOption, false, false, false>,
    'onChange' | 'renderInput' | 'options' | 'renderGroup' | 'renderOption'
  > {
  onChange: (value: TaxonomyNodeSuggestionOption) => void;
  openOnFocus?: boolean;
  onViewMore?: (option: TaxonomyNodeSuggestionOption) => void;
  renderInput?: (
    textFieldProps: TextFieldProps,
    activeOption?: TaxonomyNodeSuggestionOption,
  ) => ReactNode;
  renderOptionProp?: (
    optionProps: BoxProps,
    option: TaxonomyNodeSuggestionOption,
  ) => ReactNode;
  onLoadingChange?: (isLoading: boolean) => void;
  renderFooter?: () => ReactNode;
}

export function TaxonomyNodesGroupsAutoComplete({
  onClose,
  onChange,
  openOnFocus,
  onViewMore,
  renderInput,
  inputValue: inputValueProp,
  onInputChange: onInputChangeProp,
  onLoadingChange,
  renderOptionProp,
  renderFooter,
  ...autoCompleteProps
}: TaxonomyNodesGroupsAutoCompleteProps) {
  const [inputValue, setInputValue] = useControlled({
    controlled: inputValueProp,
    default: '',
    name: 'TaxonomyNodesGroupsAutoComplete',
    state: 'inputValue',
  });
  const [fetchTaxonomySearchGroupsAPI, { options, rows, isLoading }] =
    useFetchTaxonomySearchGroups();

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const fetchTaxonomySearchGroups = useMemo(
    () =>
      debounce((searchTerm: string) => {
        fetchTaxonomySearchGroupsAPI(searchTerm || undefined);
      }, 250),
    [fetchTaxonomySearchGroupsAPI],
  );

  useEffect(() => {
    fetchTaxonomySearchGroups(inputValue);
  }, [inputValue, fetchTaxonomySearchGroups]);

  const onInputChange = (
    eve: SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason !== 'reset') {
      setInputValue(value);
      onInputChangeProp?.(eve, value, reason);
    }
  };

  const defaultRenderInput = (params: TextFieldProps) => (
    <AutoCompleteTextField
      {...params}
      placeholder="Filter your feedback like a barista filters their coffee"
    />
  );

  const renderOption = (
    optionProps: BoxProps,
    option: TaxonomyNodeSuggestionOption,
  ) => {
    return <TaxonomyNodeSuggestion option={option} optionProps={optionProps} />;
  };

  return (
    <>
      <MultiColumnAutoComplete
        {...autoCompleteProps}
        openOnFocus={openOnFocus}
        columnsLength={1}
        inputValue={inputValue}
        onInputChange={onInputChange}
        options={options}
        rows={rows}
        loading={isLoading}
        noOptionsText="No suggestions found"
        renderInput={renderInput ?? defaultRenderInput}
        groupKey="groupID"
        groupBy={(option) => pluralize(option.groupDisplayName)}
        renderOption={renderOptionProp ?? renderOption}
        renderFooter={renderFooter}
        onClose={(eve, reason) => {
          if (reason !== 'toggleInput') {
            onClose?.(eve, reason);
          }
        }}
        onChange={(event, option) => {
          if (event.type === 'keydown' && isLoading) {
            return;
          }

          if (!option) {
            return;
          }

          if (option.id.endsWith('_more')) {
            onViewMore?.(option);
            return;
          }

          onChange(option);
        }}
        PaperProps={{
          sx: { maxHeight: '50vh' },
        }}
      />
    </>
  );
}
